import { useState, useEffect } from 'react';

import { X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useMaintenanceMode } from '@/hooks/useMaintenanceMode';

function formatTimeUntil(targetDate: string): string {
  const diff = new Date(targetDate).getTime() - Date.now();
  if (diff <= 0) return 'any moment now';

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  }
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return 'less than a minute';
}

export function MaintenanceBanner(): React.ReactElement | null {
  const { config, loading, isActive, isScheduled } = useMaintenanceMode();
  const [dismissed, setDismissed] = useState(false);
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!isScheduled || dismissed) return;
    const interval = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(interval);
  }, [isScheduled, dismissed]);

  useEffect(() => {
    if (isActive) setDismissed(false);
  }, [isActive]);

  if (loading || (!isActive && !isScheduled) || (dismissed && !isActive)) {
    return null;
  }

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-4 py-2.5 text-sm border-l-[3px]',
        isActive
          ? 'bg-[#fdf2f2] dark:bg-red-950/30 border-l-red-500 text-[#6b2126] dark:text-red-300'
          : 'bg-[#fffbeb] dark:bg-amber-950/30 border-l-amber-500 text-[#78560a] dark:text-amber-300',
      )}
    >
      <div className="flex-1">
        {isActive ? (
          <span>
            {config.message || 'System maintenance in progress. Projects are temporarily unavailable.'}
          </span>
        ) : (
          <span>
            {config.scheduledMessage || 'Scheduled maintenance upcoming.'}
            {config.scheduledAt && (
              <span className={cn(
                'ml-1.5',
                isActive
                  ? 'text-[#9b3337] dark:text-red-400'
                  : 'text-[#a07815] dark:text-amber-400',
              )}>
                Starts {formatTimeUntil(config.scheduledAt)}.
              </span>
            )}
          </span>
        )}
      </div>

      {!isActive && (
        <button
          onClick={() => setDismissed(true)}
          className="flex-shrink-0 p-0.5 rounded text-current opacity-40 hover:opacity-70 transition-opacity"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
